import type { ReputationOverviewDto } from '@up2stay/up2promo-api-client';
import { Badge, Button, Card, cn } from '@up2stay/up2promo-ui';
import { ArrowRight } from 'lucide-react';

import { ratingBarFill } from '@/core/components/reputation/reputation-chart-config';
import { REPUTATION_SECTION_TITLE_CLASS } from '@/core/components/reputation/reputation-surface';

export type OverviewReviewsCardLabels = {
  title: string;
  viewAll: string;
  outOf: string;
  googleReviews: string;
  unanswered: string;
};

const STARS = [5, 4, 3, 2, 1] as const;

export function OverviewReviewsCard({
  overview,
  labels,
  locale,
  onViewAll,
}: {
  overview: Pick<
    ReputationOverviewDto,
    | 'score'
    | 'averageRating'
    | 'totalReviews'
    | 'respondedCount'
    | 'ratingDistribution'
  >;
  labels: OverviewReviewsCardLabels;
  locale: string;
  onViewAll: () => void;
}) {
  const { score, averageRating, totalReviews, ratingDistribution } = overview;
  const unanswered = Math.max(0, totalReviews - overview.respondedCount);
  const maxCount = Math.max(...STARS.map((s) => ratingDistribution[s]), 1);

  return (
    <Card className="flex min-w-0 flex-col gap-5 p-5 sm:p-6">
      <div className="flex items-center justify-between gap-2">
        <h2 className={REPUTATION_SECTION_TITLE_CLASS}>{labels.title}</h2>
        <Button variant="ghostAccent" size="sm" onClick={onViewAll}>
          {labels.viewAll}
          <ArrowRight className="ml-1 size-3.5" aria-hidden />
        </Button>
      </div>

      <div className="flex items-center gap-[18px] max-md:hidden">
        <div
          className="flex size-[104px] shrink-0 items-center justify-center rounded-full"
          style={{
            background: `conic-gradient(var(--primary) 0 ${score.score}%, var(--muted) ${score.score}% 100%)`,
          }}
          role="img"
          aria-label={`${score.score} ${labels.outOf}`}
        >
          <div className="flex size-[84px] flex-col items-center justify-center rounded-full bg-card">
            <span className="text-[28px] font-bold leading-none tabular-nums">
              {score.score}
            </span>
            <span className="text-muted-foreground text-[10.5px]">
              {labels.outOf}
            </span>
          </div>
        </div>
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-[26px] font-bold tabular-nums">
              {averageRating.toFixed(1)}
            </span>
            <span
              className="text-[15px] tracking-wide text-[color:var(--chart-4)]"
              aria-hidden
            >
              ★★★★★
            </span>
          </div>
          <p className="text-muted-foreground mt-0.5 text-[12.5px]">
            {labels.googleReviews.replace(
              '{count}',
              totalReviews.toLocaleString(locale),
            )}
          </p>
          {unanswered > 0 ? (
            <Badge variant="warning" className="mt-2">
              {labels.unanswered.replace(
                '{count}',
                unanswered.toLocaleString(locale),
              )}
            </Badge>
          ) : null}
        </div>
      </div>

      <ul className="flex flex-col gap-2.5">
        {STARS.map((star) => {
          const count = ratingDistribution[star];
          return (
            <li key={star} className="flex items-center gap-2.5 text-xs">
              <span className="text-muted-foreground w-[22px]">{star}★</span>
              <div className="h-1.5 flex-1 rounded-full bg-muted">
                {count > 0 ? (
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(3, (count / maxCount) * 100)}%`,
                      background: ratingBarFill(star),
                    }}
                  />
                ) : null}
              </div>
              <span className="w-6 text-right font-mono tabular-nums">
                {count}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
